import type { FileRef, FolderResult, LibraryAdapter, LibraryDetail, LibraryItem, LibraryStatus, ManuscriptCheck } from "@studio-features/meta-crew/library-adapter";
import type { ReviewNotice } from "@studio-features/meta-crew/refine-data";
import type { PricedModels } from "./llm-api";
import { api, errorMessage } from "./api";
import type { Device, MachineRow } from "./types";

/**
 * 시안 원고함 부품(6001c5c)에 넘기는 실제 API 어댑터 (docs/meta-crew-manuscript-library-design.md 7절).
 * 부품은 화면 모델만 알고, 서버 DTO 이름은 여기서 바꾼다 (목록 files 수 → fileCount, 상세 fileRefs → files).
 * 실패는 서버 문구 그대로 Error 로 던진다. 상태·잠금·검사·중복은 서버 값을 그대로 넘긴다.
 * 요청서 35절(시안 db84b8e, docs/meta-crew-manuscript-ownership-design.md): 저장 범위 = "메타크루"(HQ_SCOPE, 관리자 소유 트리) +
 * 고객사별 읽기 전용 원고함(그 고객사가 등록한 원고). 고객사 폴더는 대상 표시일 뿐 소유가 아니다. 관리 콘솔에서 올리는 파일은 모두 관리자 파일(/admin/files).
 */
export const HQ_SCOPE = "hq";
const scopeParam = (scope: string | undefined) => (!scope || scope === HQ_SCOPE ? undefined : scope);

// 서버 행: notices 는 수 요약({total, warn}), noticeList 가 항목 전체 (요청서 29-5절 "검토 n"), refineOrigin 은 정형화 출처
type Row = Omit<LibraryItem, "fileCount" | "notices" | "refineOrigin"> & { files: number; notices?: { total: number; warn: number }; noticeList?: ReviewNotice[]; refineOrigin?: { id: string; seq: number } | null };
type DetailDto = Row & {
  body: string;
  fileRefs: FileRef[];
  revisions: LibraryDetail["revisions"];
  uses: (LibraryDetail["uses"][number] & { runStatus: string | null })[];
  sameBody: LibraryDetail["sameBody"];
  problems?: { errors?: string[]; warnings?: string[]; notices?: ReviewNotice[] };
};
type CheckDto = Omit<ManuscriptCheck, "blocks"> & { blocks: { type: string; text?: string; url?: string; files?: { name: string; caption: string }[]; layout?: string }[] };

const item = ({ files, notices: _summary, noticeList, refineOrigin, ...r }: Row): LibraryItem => ({ ...r, fileCount: files, lastRunStatus: null, notices: noticeList ?? [], refineOrigin: refineOrigin ?? undefined });
const qs = (o: Record<string, string | number | undefined>) =>
  "?" +
  Object.entries(o)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join("&");
const ids = (files: FileRef[]) => files.map((f) => ({ fileId: f.fileId }));

/** 서버 오류 문구를 화면이 그대로 보이도록. */
async function call<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    throw new Error(errorMessage(e));
  }
}

export function createLibraryAdapter(customers: LibraryAdapter["customers"]): LibraryAdapter {
  // 인증이 필요한 첨부는 object URL 로. 여러 미리보기가 같은 URL 을 쓰므로 어댑터가 캐시하고 수명을 맡는다.
  const urls = new Map<string, Promise<string>>();
  return {
    customers,
    defaultScopeId: HQ_SCOPE,
    // 메타크루 트리 + 고객사 원고함(읽기 전용: 고객사가 등록한 원고, 직원은 열람만)
    scopes: [{ id: HQ_SCOPE, name: "메타크루", ownerCustomerId: null, readOnly: false }, ...customers.map((c) => ({ id: c.id, name: c.name, ownerCustomerId: c.id, readOnly: true }))],
    folders: (scope) =>
      call(async () => {
        const r = await api.get<FolderResult>(`/admin/manuscript-folders?owner=${encodeURIComponent(scope || HQ_SCOPE)}`);
        // 상위 폴더에 대상이 있으면 상속 (서버도 같은 규칙으로 저장)
        const byId = new Map(r.folders.map((f) => [f.id, f]));
        return { ...r, folders: r.folders.map((f) => ({ ...f, targetInherited: !!f.parentId && !!byId.get(f.parentId)?.targetCustomerId })) };
      }),
    list: (q) =>
      call(async () => {
        const r = await api.page<Row>(
          `/admin/manuscripts${qs({
            ownerCustomerId: q.customerId || HQ_SCOPE,
            folderId: q.folderId === "all" ? undefined : q.folderId,
            deep: q.folderId !== "all" && q.folderId !== "none" && q.deep ? "true" : undefined,
            status: q.status,
            q: q.q?.trim(),
            offset: q.offset,
            limit: q.limit,
          })}`,
        );
        return { items: r.items.map(item), total: r.total };
      }),
    detail: (id) =>
      call(async () => {
        const { fileRefs, body, revisions, uses, sameBody, problems, ...r } = await api.get<DetailDto>(`/admin/manuscripts/${id}`);
        return { ...item(r), notices: problems?.notices ?? r.noticeList ?? [], body, files: fileRefs, revisions, uses, sameBody };
      }),
    revision: (id, rev) => call(() => api.get<{ body: string; files: FileRef[] }>(`/admin/manuscripts/${id}/revisions/${rev}`)),
    check: (customerId, body, files) =>
      call(async () => {
        const r = await api.post<CheckDto>("/admin/manuscripts/check", { customerId: scopeParam(customerId), body, files: ids(files) });
        const fid = (name: string) => r.media.find((m) => m.name.toLowerCase() === name.toLowerCase())?.fileId ?? null;
        return { ...r, blocks: r.blocks.map((b) => (b.files ? { ...b, files: b.files.map((f) => ({ ...f, fileId: fid(f.name) })) } : b)) };
      }),
    // 관리 콘솔에서 올리는 원고 첨부는 관리자 파일 (고객사 폴더에 넣어도 파일은 옮기지 않음)
    uploadFile: (_scope, file) => call(() => uploadAdminFile(file)),
    // 직원은 모든 원고를 보므로 첨부도 소유와 관계없이 같은 경로 (기존 원고의 고객사 파일 포함)
    fileUrl: (_scope, fileId) => {
      let u = urls.get(fileId);
      if (!u) {
        u = call(() => api.blobUrl(`/admin/files/${fileId}/content`));
        u.catch(() => urls.delete(fileId));
        urls.set(fileId, u);
      }
      return u;
    },
    create: (input) =>
      call(async () => {
        const r = await api.post<{ manuscript: Row }>("/admin/manuscripts", { customerId: scopeParam(input.customerId), folderId: input.folderId, body: input.body, files: ids(input.files), source: "editor" });
        return item(r.manuscript);
      }),
    revise: (id, input) =>
      call(async () => {
        const r = await api.put<{ manuscript: Row; changed: boolean }>(`/admin/manuscripts/${id}`, { body: input.body, files: ids(input.files), revision: input.revision });
        return { item: item(r.manuscript), changed: r.changed };
      }),
    patch: (id, input) => call(async () => item(await api.patch<Row>(`/admin/manuscripts/${id}`, input))),
    bulk: (list, action, folderId) =>
      call(async () => (await api.post<{ items: { id: string; ok: boolean; error?: string }[] }>("/admin/manuscripts/bulk", { ids: list, action, ...(action === "move" ? { folderId: folderId ?? null } : {}) })).items),
    remove: (id) => call(async () => void (await api.del(`/admin/manuscripts/${id}`))),
    createFolder: (_scope, parentId, name, targetCustomerId) => call(async () => void (await api.post("/admin/manuscript-folders", { parentId, name, ...(targetCustomerId ? { targetCustomerId } : {}) }))),
    updateFolder: (id, input) => call(async () => void (await api.patch(`/admin/manuscript-folders/${id}`, input))),
    deleteFolder: (id) => call(async () => void (await api.del(`/admin/manuscript-folders/${id}`))),
    batch: (customerId, folderId, items) =>
      call(
        async () =>
          (
            await api.post<{ items: Awaited<ReturnType<LibraryAdapter["batch"]>> }>("/admin/manuscripts/batch", {
              customerId: scopeParam(customerId),
              folderId,
              items: items.map((i) => ({ ...(i.path ? { path: i.path } : {}), body: i.body, files: ids(i.files), keywords: i.keywords, ...(i.memo ? { memo: i.memo.slice(0, 500) } : {}) })),
            })
          ).items,
      ),
    machines: (customerId) =>
      call(async () =>
        // 배정 가능한 PC = 페어링됐고 폐기되지 않은 머신 (작업 등록·재시도의 "PC 없음" 경고와 같은 기준)
        (await api.page<MachineRow>(`/c/${customerId}/machines`)).items.filter((m) => m.pairedAt && m.status !== "retired").map((m) => ({ id: m.id, label: m.label })),
      ),
    devices: (customerId, machineId) =>
      call(async () =>
        (await api.page<Device>(`/c/${customerId}/devices`)).items
          .filter((d) => !machineId || d.machineId === machineId)
          .map((d) => ({ serial: d.serial, label: (d.alias ?? d.serial) + (d.model ? " · " + d.model : "") })),
      ),
    // 원고함 보내기 2단계의 AI 모델 선택 (직원만, /admin/llm/models)
    models: () => call(() => api.get<PricedModels>("/admin/llm/models")),
    send: (input) => call(async () => (await api.post<{ items: Awaited<ReturnType<LibraryAdapter["send"]>> }>("/admin/manuscripts/send", input)).items),
  };
}

export type { LibraryStatus };

/** 관리자 파일 올리기 (원고함·정형화 공통, 원고 소유 설계 2-2) */
export async function uploadAdminFile(file: File): Promise<FileRef> {
  const r = await api.upload<{ id: string; name?: string }>("/admin/files", file);
  return { fileId: r.id, name: r.name ?? file.name };
}
